import type { GetProcessDraftResponse } from '@bliks-io/contracts';
import { transformDraftNode } from '../../draftGraph/core';

export type DraftNode =
  | GetProcessDraftResponse['steps'][number]
  | GetProcessDraftResponse['draftAssignments'][number];

/**
 * Strip deleted steps' stableStepIds from every `previous_output` input's
 * `sourceSteps`. Empty `previous_output` inputs are dropped — matches the
 * server-side cleanup applied when a step is removed from the graph.
 * Set-based so a bulk deletion strips all ids in ONE pass over the inputs.
 */
export function stripStableIdsFromInputs<T extends DraftNode>(
  node: T,
  deletedStableStepIds: ReadonlySet<string>
): T {
  if (!node.inputs?.length || deletedStableStepIds.size === 0) return node;
  let changed = false;
  const nextInputs: unknown[] = [];
  for (const input of node.inputs) {
    if (input.inputType !== 'previous_output' || !input.sourceSteps?.length) {
      nextInputs.push(input);
      continue;
    }
    const filtered = input.sourceSteps.filter(
      (src) => !deletedStableStepIds.has(String(src.stepId))
    );
    if (filtered.length === input.sourceSteps.length) {
      nextInputs.push(input);
      continue;
    }
    changed = true;
    if (filtered.length === 0) continue;
    nextInputs.push({ ...input, sourceSteps: filtered });
  }
  return changed ? ({ ...node, inputs: nextInputs } as unknown as T) : node;
}

export function extractRuleIdSet(rules: ReadonlyArray<{ _id?: unknown }> | undefined): Set<string> {
  if (!rules) return new Set();
  const ids = new Set<string>();
  for (const r of rules) {
    if (r._id) ids.add(String(r._id));
  }
  return ids;
}

export function inputReferencesSource(
  input: DraftNode['inputs'][number],
  sourceStableId: string
): boolean {
  if (input.inputType !== 'previous_output') return false;
  return (input.sourceSteps ?? []).some((src) => String(src.stepId) === sourceStableId);
}

export function addSourceToNodeInputs<T extends DraftNode>(
  node: T,
  sourceStableId: string,
  ruleIds?: readonly string[]
): T {
  const inputs = node.inputs ?? [];
  const existingIdx = inputs.findIndex((input) => inputReferencesSource(input, sourceStableId));
  if (existingIdx !== -1) {
    // The target already has an edge from this source. A decision's second
    // output wiring to the same target (or a disconnect-undo of one output)
    // unions the new output group into the existing entry — mirroring the
    // server connect-union so optimistic state matches the response. A
    // ruleId-less reconnect (or a group already present) is a no-op.
    if (!ruleIds || ruleIds.length === 0) return node;
    const input = inputs[existingIdx];
    if (input.inputType !== 'previous_output') return node;
    let changed = false;
    const nextSources = (input.sourceSteps ?? []).map((src) => {
      if (String(src.stepId) !== sourceStableId) return src;
      const existing = new Set((src.ruleIds ?? []).map(String));
      const toAdd = ruleIds.filter((r) => !existing.has(String(r)));
      if (toAdd.length === 0) return src;
      changed = true;
      return { ...src, ruleIds: [...(src.ruleIds ?? []), ...toAdd] };
    });
    if (!changed) return node;
    const nextInputs = inputs.map((inp, i) =>
      i === existingIdx ? { ...inp, sourceSteps: nextSources } : inp
    );
    return { ...node, inputs: nextInputs } as unknown as T;
  }
  const newInput = {
    inputType: 'previous_output' as const,
    name: '',
    description: '',
    sourceSteps: [
      {
        stepId: sourceStableId,
        ...(ruleIds && ruleIds.length > 0 ? { ruleIds: [...ruleIds] } : {})
      }
    ]
  };
  return {
    ...node,
    inputs: [...inputs, newInput]
  } as unknown as T;
}

/**
 * Walk a node's `previous_output` inputs and rewrite the `sourceSteps` entry
 * matching `sourceStableId` by filtering its `ruleIds` through `keepRuleId`:
 *  - a task-style entry (no `ruleIds`) is left untouched — rule scoping can't
 *    apply to it;
 *  - an entry whose `ruleIds` all drop is removed (an empty decision `ruleIds`
 *    is "unbound", which the builder drops);
 *  - an input left with no sources is removed.
 * Reference equality is preserved when nothing changes so optimistic cache
 * updates stay cheap across unaffected nodes. The single iteration/bookkeeping
 * skeleton shared by the output-scoped disconnect and the stale-rule prune.
 */
function filterRuleIdsOnSource<T extends DraftNode>(
  node: T,
  sourceStableId: string,
  keepRuleId: (ruleId: string) => boolean
): T {
  const inputs = node.inputs ?? [];
  let anyInputChanged = false;
  const nextInputs: unknown[] = [];
  for (const input of inputs) {
    if (input.inputType !== 'previous_output') {
      nextInputs.push(input);
      continue;
    }
    let sourcesChanged = false;
    const nextSources: unknown[] = [];
    for (const src of input.sourceSteps ?? []) {
      const ruleIds = src.ruleIds ?? [];
      if (String(src.stepId) !== sourceStableId || ruleIds.length === 0) {
        nextSources.push(src);
        continue;
      }
      const kept = ruleIds.filter((rid) => keepRuleId(String(rid)));
      if (kept.length === ruleIds.length) {
        nextSources.push(src);
        continue;
      }
      sourcesChanged = true;
      if (kept.length === 0) continue;
      nextSources.push({ ...src, ruleIds: kept });
    }
    if (!sourcesChanged) {
      nextInputs.push(input);
      continue;
    }
    anyInputChanged = true;
    if (nextSources.length === 0) continue;
    nextInputs.push({ ...input, sourceSteps: nextSources });
  }
  return anyInputChanged ? ({ ...node, inputs: nextInputs } as unknown as T) : node;
}

/**
 * Remove an incoming edge from `sourceStableId`. When `outputRuleIds` is given
 * (the clicked decision output's rule group), trim only those ids from the
 * matching `sourceSteps` entry — other outputs of the same decision and fan-in
 * siblings survive; an entry whose ruleIds empties is dropped (unbound), and an
 * input with no sources left is dropped. Without `outputRuleIds`, the whole
 * connection from the source is removed (task / legacy disconnect).
 */
export function removeSourceFromNodeInputs<T extends DraftNode>(
  node: T,
  sourceStableId: string,
  outputRuleIds?: readonly string[]
): T {
  if (!outputRuleIds || outputRuleIds.length === 0) {
    const inputs = node.inputs ?? [];
    const filtered = inputs.filter((input) => !inputReferencesSource(input, sourceStableId));
    return filtered.length === inputs.length
      ? node
      : ({ ...node, inputs: filtered } as unknown as T);
  }

  const groupSet = new Set(outputRuleIds.map(String));
  return filterRuleIdsOnSource(node, sourceStableId, (rid) => !groupSet.has(rid));
}

/**
 * Swap a node's incoming edge from `fromStableId` to `toStableId` — removes
 * every `sourceSteps` entry tagged with `fromStableId` and appends a fresh
 * reference to `toStableId` (no ruleIds; callers that need rule tagging on
 * the new edge should compose `removeSourceFromNodeInputs` + `addSourceToNodeInputs`
 * directly).
 */
export function swapSourceOnNode<T extends DraftNode>(
  node: T,
  fromStableId: string,
  toStableId: string
): T {
  return addSourceToNodeInputs(removeSourceFromNodeInputs(node, fromStableId), toStableId);
}

/**
 * Rewrite every `sourceSteps` entry referencing `fromStableId` to instead
 * reference `toStableId`. Preserves reference equality when nothing changes.
 */
export function rewriteSourceStepId<T extends DraftNode>(
  node: T,
  fromStableId: string,
  toStableId: string
): T {
  const inputs = node.inputs ?? [];
  let anyInputChanged = false;
  const nextInputs: unknown[] = [];
  for (const input of inputs) {
    if (input.inputType !== 'previous_output' || !input.sourceSteps?.length) {
      nextInputs.push(input);
      continue;
    }
    let sourcesChanged = false;
    const nextSources = input.sourceSteps.map((src) => {
      if (String(src.stepId) !== fromStableId) return src;
      sourcesChanged = true;
      return { ...src, stepId: toStableId };
    });
    if (!sourcesChanged) {
      nextInputs.push(input);
      continue;
    }
    anyInputChanged = true;
    nextInputs.push({ ...input, sourceSteps: nextSources });
  }
  if (!anyInputChanged) return node;
  return { ...node, inputs: nextInputs } as unknown as T;
}

/**
 * Frontend mirror of the backend `pruneStaleRuleReferencesDownstream` pass.
 * Run against every downstream node (step or assignment) after a rules patch
 * dropped one or more rule ids: strip stale ids from the matching
 * `sourceSteps` entry, drop the entry entirely when all its ruleIds were
 * deleted, and drop the input when its last sourceSteps entry goes.
 *
 * Preserves reference equality when nothing changes so optimistic cache
 * updates stay cheap across downstream nodes that weren't affected.
 */
export function pruneStaleRuleIdsFromInputs<T extends DraftNode>(
  node: T,
  sourceStableId: string,
  liveRuleIds: ReadonlySet<string>
): T {
  return filterRuleIdsOnSource(node, sourceStableId, (rid) => liveRuleIds.has(rid));
}

type PreviousOutputSource = NonNullable<
  Extract<DraftNode['inputs'][number], { inputType: 'previous_output' }>['sourceSteps']
>[number];

/**
 * Reconcile a single `sourceSteps` entry pointing at a just-converted step.
 * Returns `null` to drop the entry, the original entry (ref-equal) when it's
 * already valid for the new kind, or a rewritten entry otherwise.
 */
function reconcileKindChangeSource(
  src: PreviousOutputSource,
  newKind: 'task' | 'decision'
): PreviousOutputSource | null {
  const hasRuleIds = (src.ruleIds ?? []).length > 0;
  if (newKind === 'decision') {
    // Drop dangling task-style edges; rule-tagged ones stay valid.
    return hasRuleIds ? src : null;
  }
  // newKind === 'task': shed the now-dead rule tag, keep the connection.
  return hasRuleIds ? { ...src, ruleIds: undefined } : src;
}

/**
 * Frontend mirror of the backend `reconcileDownstreamOnKindChange` pass. After
 * a step's `actionType` flips, rewrite every downstream node's `sourceSteps`
 * that pointed at it:
 * - to a **decision**: drop task-style entries (no `ruleIds`) — they can't
 *   bind to a rule and would otherwise paint a phantom edge onto the first
 *   rule.
 * - to a **task**: strip the now-dead `ruleIds`, keeping the connection (a
 *   task has a single output).
 *
 * Drops an input whose `sourceSteps` empties out, and preserves reference
 * equality when nothing changes so unaffected downstream nodes stay cheap.
 */
export function reconcileDownstreamInputsOnKindChange<T extends DraftNode>(
  node: T,
  sourceStableId: string,
  newKind: 'task' | 'decision'
): T {
  const inputs = node.inputs ?? [];
  let anyInputChanged = false;
  const nextInputs: unknown[] = [];
  for (const input of inputs) {
    if (input.inputType !== 'previous_output') {
      nextInputs.push(input);
      continue;
    }
    const sources = input.sourceSteps ?? [];
    let sourcesChanged = false;
    const nextSources: unknown[] = [];
    for (const src of sources) {
      if (!src.stepId || String(src.stepId) !== sourceStableId) {
        nextSources.push(src);
        continue;
      }
      const reconciled = reconcileKindChangeSource(src, newKind);
      if (reconciled === null) {
        sourcesChanged = true;
        continue;
      }
      if (reconciled !== src) sourcesChanged = true;
      nextSources.push(reconciled);
    }
    if (!sourcesChanged) {
      nextInputs.push(input);
      continue;
    }
    anyInputChanged = true;
    if (nextSources.length === 0) continue;
    nextInputs.push({ ...input, sourceSteps: nextSources });
  }
  if (!anyInputChanged) return node;
  return { ...node, inputs: nextInputs } as unknown as T;
}

/**
 * Frontend mirror of the backend `derivePreviousStepFromInputs` (ADR 0006): a
 * step's `previousStep` cache is a derivation of its edge set, never authored
 * independently. A `previous_output` source naming a start event node wins,
 * else the first `previous_output` source, else a start-linked event input,
 * else null. Kept in lockstep with the backend so the optimistic value matches
 * the mutation response that replaces it.
 */
export function deriveStepPreviousStep(
  inputs: DraftNode['inputs'] | undefined,
  startEventIds: ReadonlySet<string>
): string | null {
  let firstPrev: string | null = null;
  let startAnchor: string | null = null;
  for (const input of inputs ?? []) {
    if (
      startAnchor === null &&
      input.inputType === 'event' &&
      input.eventId &&
      startEventIds.has(String(input.eventId))
    ) {
      startAnchor = String(input.eventId);
    }
    if (input.inputType !== 'previous_output') continue;
    for (const src of input.sourceSteps ?? []) {
      if (!src.stepId) continue;
      const id = String(src.stepId);
      if (startEventIds.has(id)) return id;
      if (firstPrev === null) firstPrev = id;
    }
  }
  return firstPrev ?? startAnchor;
}

/**
 * Apply an edge transform to the connect/disconnect target — a step OR an
 * assignment, addressed by `_id` (with `stableStepId` accepted too, courtesy
 * of `transformDraftNode`'s lookup). The source must be a step; its
 * `stableStepId` is what the target's `sourceSteps` reference.
 *
 * For a STEP target, `previousStep` is resynced from the post-transform edge
 * set (ADR 0006) so the canvas doesn't keep a stale predecessor — and the
 * builder doesn't re-synthesize a phantom edge — until the server response
 * lands. Assignments carry no `previousStep` cache, so they pass through.
 */
export function applyEdgeChangeToTarget(
  old: GetProcessDraftResponse,
  sourceStepId: string,
  targetStepId: string,
  transform: <T extends DraftNode>(target: T, sourceStable: string) => T
): GetProcessDraftResponse {
  const source = old.steps.find((s) => s._id === sourceStepId);
  if (!source) return old;
  const sourceStable = String(source.stableStepId);
  const startEventIds = new Set(
    (old.events ?? []).filter((e) => e.eventKind === 'start').map((e) => String(e.stableStepId))
  );
  return transformDraftNode(old, targetStepId, (entry) => {
    if (entry.kind === 'event') return entry.node;
    if (entry.kind === 'assignment') return transform(entry.node, sourceStable);
    const transformed = transform(entry.node, sourceStable);
    const derived = deriveStepPreviousStep(transformed.inputs, startEventIds);
    if (String(transformed.previousStep ?? null) === String(derived ?? null)) {
      return transformed;
    }
    return { ...transformed, previousStep: derived };
  });
}
